import { Link } from "react-router-dom";
import { ArrowRight, ChefHat, Utensils, Leaf } from "lucide-react";
import { IMG, CONTACT, LOGO_URL } from "@/data/content";

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 pt-16 lg:pt-24 pb-20">
        <div className="flex flex-col items-center text-center">
          <img
            src={LOGO_URL}
            alt="Out of Eden"
            className="h-40 sm:h-52 lg:h-64 w-auto fade-up"
            data-testid="hero-logo"
          />
          <div className="eyebrow mt-8 fade-up delay-1">Catering · Private Chef · Meal Prep — London, ON</div>
          <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-[96px] leading-[0.95] mt-6 fade-up delay-2" style={{ color: "var(--ooe-ink)" }}>
            Feel Good <em className="not-italic" style={{ color: "var(--ooe-brand)" }}>Food.</em>
          </h1>
          <p className="mt-8 max-w-xl text-[17px] leading-relaxed fade-up delay-3" style={{ color: "var(--ooe-ink-2)" }}>
            We curate exceptional culinary experiences through thoughtfully prepared,
            nutrient-rich cuisine — crafted from the finest ingredients and served
            with sophistication.
          </p>
          <div className="mt-10 flex flex-wrap gap-5 items-center justify-center fade-up delay-4">
            <Link to="/contact" className="btn-primary" data-testid="hero-book-btn">
              Book Your Experience <ArrowRight size={16} />
            </Link>
            <Link to="/menus" className="btn-ghost" data-testid="hero-menus-btn">
              View the Menus <ArrowRight size={14} />
            </Link>
          </div>
        </div>

        <div className="relative mt-20 fade-up delay-4">
          <img
            src={IMG.chef_sauce}
            alt="Chef finishing a plated dish"
            className="w-full h-[420px] lg:h-[560px] object-cover"
            style={{ border: "1px solid var(--ooe-border)" }}
          />
          <div
            className="absolute -bottom-6 left-6 lg:left-12 px-6 py-4 hidden md:block"
            style={{ background: "var(--ooe-bg)", border: "1px solid var(--ooe-border)" }}
          >
            <div className="eyebrow" style={{ fontSize: 10 }}>Est. London, Ontario</div>
            <div className="font-serif-display text-xl mt-1" style={{ color: "var(--ooe-brand)" }}>
              Restaurant-quality, at your table.
            </div>
          </div>
        </div>
      </section>

      {/* MISSION */}
      <section className="mt-32 lg:mt-40" style={{ background: "var(--ooe-bg-2)" }}>
        <div className="max-w-[1100px] mx-auto px-6 lg:px-12 py-24 lg:py-32 text-center">
          <div className="eyebrow mb-8">Our Mission</div>
          <p className="font-serif-display text-3xl sm:text-4xl lg:text-5xl leading-[1.25]" style={{ color: "var(--ooe-ink)" }}>
            Out of Eden focuses on fresh, wholesome food made with care. We
            choose <em style={{ color: "var(--ooe-brand)" }}>high-quality proteins</em>, Ontario produce and fruit, and fresh
            herbs to create healthy meals that are both nourishing and
            delicious. Every dish is prepared with attention to quality and
            flavour, so you can enjoy food that feels good and tastes great.
          </p>
        </div>
      </section>

      {/* SERVICES */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24 lg:py-32">
        <div className="grid lg:grid-cols-12 gap-10 items-end mb-14">
          <div className="lg:col-span-7">
            <div className="eyebrow mb-4">What we do</div>
            <h2 className="font-serif-display text-4xl lg:text-6xl" style={{ color: "var(--ooe-ink)" }}>
              Three ways to experience the kitchen.
            </h2>
          </div>
          <div className="lg:col-span-5 text-[15px]" style={{ color: "var(--ooe-ink-2)" }}>
            Whether it's a private event, a quiet dinner at home, or a week of
            chef-crafted meals — every service is grounded in the same principles
            of precision, sustainability, and elegance.
          </div>
        </div>

        <div className="grid md:grid-cols-3" style={{ borderTop: "1px solid var(--ooe-border)", borderLeft: "1px solid var(--ooe-border)" }}>
          {[
            { icon: <Utensils size={22} />, title: "Catering", desc: "Bespoke menus for corporate meetings, backyard gatherings, and intimate at-home multi-course menus.", to: "/services#catering" },
            { icon: <ChefHat size={22} />, title: "Private Chef", desc: "Restaurant-quality cuisine, prepared in your home, with discretion and creativity.", to: "/services#private-chef", comingSoon: true },
            { icon: <Leaf size={22} />, title: "Meal Prep", desc: "Chef-crafted, nutritionally balanced meals tailored to your lifestyle and preferences.", to: "/services#meal-prep" },
          ].map((s) => (
            <div
              key={s.title}
              className="p-10 lg:p-12 flex flex-col gap-6"
              style={{ borderRight: "1px solid var(--ooe-border)", borderBottom: "1px solid var(--ooe-border)" }}
              data-testid={`service-card-${s.title.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <div style={{ color: "var(--ooe-brand)" }}>{s.icon}</div>
              <h3 className="font-serif-display text-3xl flex flex-wrap items-baseline gap-x-3" style={{ color: "var(--ooe-ink)" }}>
                {s.title}
                {s.comingSoon && (
                  <span className="eyebrow" style={{ fontSize: 10, color: "var(--ooe-brand)" }}>Coming September 2026</span>
                )}
              </h3>
              <p className="text-[15px] leading-relaxed" style={{ color: "var(--ooe-ink-2)" }}>{s.desc}</p>
              <Link to={s.to} className="btn-ghost mt-auto self-start">Learn more <ArrowRight size={14} /></Link>
            </div>
          ))}
        </div>
      </section>

      {/* INGREDIENTS COLLAGE */}
      <section className="py-24 lg:py-32" style={{ background: "var(--ooe-bg-2)" }}>
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-5">
              <div className="eyebrow mb-5">The Ingredients</div>
              <h2 className="font-serif-display text-4xl lg:text-5xl leading-tight" style={{ color: "var(--ooe-ink)" }}>
                Everything begins with what we choose.
              </h2>
              <p className="mt-6 text-[15px] leading-relaxed" style={{ color: "var(--ooe-ink-2)" }}>
                We place the highest importance on local, high-quality
                ingredients and embrace seasonal cooking to ensure every meal
                is fresh, flavourful and thoughtfully prepared.
              </p>
              <Link to="/about" className="btn-ghost mt-8 inline-flex">Our philosophy <ArrowRight size={14} /></Link>
            </div>
            <div className="lg:col-span-7 grid grid-cols-6 grid-rows-2 gap-4 h-[520px]">
              <img src={IMG.romanesco_carrots} alt="" className="col-span-3 row-span-2 w-full h-full object-cover gallery-img" />
              <img src={IMG.herbs} alt="" className="col-span-3 row-span-1 w-full h-full object-cover gallery-img" />
              <img src={IMG.mirepoix} alt="" className="col-span-3 row-span-1 w-full h-full object-cover gallery-img" />
            </div>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="py-10 overflow-hidden border-y" style={{ borderColor: "var(--ooe-border)" }}>
        <div className="flex marquee-track whitespace-nowrap font-serif-display text-3xl lg:text-5xl" style={{ color: "var(--ooe-brand)" }}>
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="flex items-center gap-12 px-6 shrink-0">
              <span>Catering</span><span>—</span>
              <span>Private Chef</span><span>—</span>
              <span>Meal Prep</span><span>—</span>
              <span>London, Ontario</span><span>—</span>
              <span>Feel Good Food</span><span>—</span>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24 lg:py-32 text-center">
        <div className="eyebrow mb-6">Let's begin</div>
        <h2 className="font-serif-display text-4xl lg:text-6xl max-w-3xl mx-auto" style={{ color: "var(--ooe-ink)" }}>
          Tell us about your table — we'll craft the rest.
        </h2>
        <div className="mt-10 flex justify-center gap-4 flex-wrap">
          <Link to="/contact" className="btn-primary" data-testid="cta-contact-btn">Start an Inquiry <ArrowRight size={16} /></Link>
          <a href={`tel:${CONTACT.phone.replace(/-/g, "")}`} className="btn-ghost" data-testid="cta-call-btn">Call {CONTACT.phone}</a>
        </div>
      </section>
    </div>
  );
}
